import React from "react";

import { FavoriteButton } from "@/components/ui/favorite-button/FavoriteButton";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import {
  LadyDtoToggleMediaFavorite,
  useFavorite,
} from "@/features/lady-profile/hooks";
import { LadyProfileDto } from "@/features/lady-profile/models";

import { LadySliderModalMedia } from "../../../models";
import { useOpenModal } from "../../../utils/openModal";
import { MediaItemRendererPropTypes, SliderModal } from "../Slider.modal";
import { PrivatePhotoAttachment } from "../components";
import { SliderPhoto } from "../components/slider-photo";
import { SliderVideo } from "../components/slider-video";
import { useSliderContext } from "../context/Slider.context";
import {
  ladySliderV2MediaFilter,
  ladySliderV2MediaPredicates,
} from "./helpers";

const { isIntroVideo, isLadyPhoto } = ladySliderV2MediaPredicates;

type LadySliderV2PropTypes = {
  data: LadyProfileDto;
  toggleMediaFavorite: LadyDtoToggleMediaFavorite;
};

export const LadySliderV2: React.FC<LadySliderV2PropTypes> = ({
  data,
  toggleMediaFavorite,
}) => {
  const { removeOne } = useOpenModal();

  const MediaItemRenderer = React.useCallback(
    (prop: MediaItemRendererPropTypes<LadySliderModalMedia>) => {
      return (
        <LadySliderItemRenderer
          {...prop}
          isOnline={data.forms.isOnline}
          ladyId={data.forms.id}
          toggleMediaFavorite={toggleMediaFavorite}
          alt={`${data?.forms.name} from ${data?.forms.city}`}
        />
      );
    },
    [data.forms, toggleMediaFavorite],
  );

  return (
    <SliderModal
      media={ladySliderV2MediaFilter(data?.forms.media ?? [])}
      onClose={removeOne}
      MediaItemRenderer={MediaItemRenderer}
    />
  );
};

type LadySliderItemRendererProps =
  MediaItemRendererPropTypes<LadySliderModalMedia> & {
    toggleMediaFavorite: LadyDtoToggleMediaFavorite;
    alt: string;
    isOnline?: boolean;
    ladyId?: number;
  };
const LadySliderItemRenderer: React.FC<LadySliderItemRendererProps> = ({
  item,
  index,
  isSelected,
  toggleMediaFavorite,
  alt,
  isOnline,
  ladyId,
}) => {
  const favoriteHookParams = ((): Parameters<typeof useFavorite>["0"] => {
    if (isLadyPhoto(item)) {
      return {
        favoriteType: "PHOTO",
        objectId: item.photoId,
        isFavorite: item.isFavorite,
      };
    }
    return {
      favoriteType: "VIDEO",
      objectId: item.id,
      isFavorite: item.isFavorite,
    };
  })();

  const { isZoomed } = useSliderContext();

  const { onFavouriteClick } = useFavorite(favoriteHookParams);
  const handleFavoriteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (favoriteHookParams.objectId && toggleMediaFavorite) {
      toggleMediaFavorite({
        objectId: favoriteHookParams.objectId,
        mediaType:
          favoriteHookParams.favoriteType === "PHOTO" ? "photo" : "intro_video",
      });
    }

    onFavouriteClick(event);
  };

  const { isAuthenticated } = useAuthContext();

  const favoriteButtonRenderCondition =
    isAuthenticated &&
    !isZoomed &&
    isSelected &&
    ((isLadyPhoto(item) && item.photoType !== "HIDDEN_PRIVATE_PHOTO") ||
      isIntroVideo(item));

  const mediaTypeForLabel = item.type === "photo" ? "photo" : "video";
  const favoriteButtonAriaLabel = `${
    item.isFavorite
      ? `Remove ${mediaTypeForLabel} from favorite`
      : `Add ${mediaTypeForLabel} to favorite`
  }`;

  return (
    <>
      {isLadyPhoto(item) && (
        <>
          <SliderPhoto
            src={item.photoSrcs.large}
            alt={alt}
            isSelected={isSelected}
            id={`profile-photo-${index}`}
          />

          {isSelected && item.photoType === "HIDDEN_PRIVATE_PHOTO" && (
            <PrivatePhotoAttachment isOnline={isOnline} ladyId={ladyId} />
          )}
        </>
      )}

      {isIntroVideo(item) && (
        <SliderVideo
          index={index}
          isSelected={isSelected}
          source={item.source || ""}
        />
      )}

      {favoriteButtonRenderCondition && (
        <FavoriteButton
          isFavorite={item.isFavorite}
          onClick={handleFavoriteClick}
          ariaLabel={favoriteButtonAriaLabel}
        />
      )}
    </>
  );
};
